import React, { useState, useEffect, useMemo } from 'react';
import { XIcon, InfoIcon, LoaderIcon } from 'lucide-react';
import { useCreateInvoice, useUpdateInvoice } from '@/hooks/useInvoices';
import { useInvoiceSettings } from '@/hooks/useInvoiceSettings';
import type { Invoice, CreateInvoiceInput } from '@/types/invoice';
import type { Payment } from '@/hooks/useAllPayments';

interface InvoiceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  clientId: string;
  clientName: string;
  clientPhone: string;
  payment?: Payment;
  editingInvoice?: Invoice | null;
}

type FormState = {
  clientId: string;
  paymentId: string;
  invoiceDate: string;
  dueDate: string;
  totalAmount: string;
  taxRate: string;
  hsnSacCode: string;
  serviceDescription: string;
  notes: string;
  clientGstin: string;
  clientBusinessName: string;
  clientBillingAddress: string;
  clientStateCode: string;
};

function getCurrentLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function InvoiceFormModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  clientId,
  clientName,
  clientPhone,
  payment,
  editingInvoice,
}: InvoiceFormModalProps) {
  const { data: settings } = useInvoiceSettings();
  const createMutation = useCreateInvoice();
  const updateMutation = editingInvoice
    ? useUpdateInvoice(editingInvoice.id)
    : null;

  const isEditMode = Boolean(editingInvoice);
  const isLoading = createMutation.isPending || updateMutation?.isPending;

  // Initialize form state
  const [formState, setFormState] = useState<FormState>(() => ({
    clientId: clientId,
    paymentId: payment?.id || '',
    invoiceDate: getCurrentLocalDate(),
    dueDate: '',
    totalAmount: payment?.amount.toString() || '',
    taxRate: settings?.default_tax_rate?.toString() || '18',
    hsnSacCode: settings?.default_hsn_sac_code || '999311',
    serviceDescription: settings?.default_service_description || '',
    notes: payment?.notes || '',
    clientGstin: '',
    clientBusinessName: '',
    clientBillingAddress: '',
    clientStateCode: '',
  }));

  const [formError, setFormError] = useState<string | null>(null);

  // Update form when settings load
  useEffect(() => {
    if (settings && !isEditMode) {
      setFormState((prev) => ({
        ...prev,
        taxRate: settings.default_tax_rate?.toString() || '18',
        hsnSacCode: settings.default_hsn_sac_code || '999311',
        serviceDescription:
          prev.serviceDescription || settings.default_service_description || '',
      }));
    }
  }, [settings, isEditMode]);

  // Load editing invoice data
  useEffect(() => {
    if (isOpen && editingInvoice) {
      setFormState({
        clientId: editingInvoice.client_id,
        paymentId: editingInvoice.payment_id || '',
        invoiceDate: editingInvoice.invoice_date.split('T')[0],
        dueDate: editingInvoice.due_date?.split('T')[0] || '',
        totalAmount: editingInvoice.total_amount.toString(),
        taxRate: editingInvoice.tax_rate.toString(),
        hsnSacCode: editingInvoice.hsn_sac_code,
        serviceDescription: editingInvoice.service_description,
        notes: editingInvoice.notes || '',
        clientGstin: editingInvoice.client_gstin || '',
        clientBusinessName: editingInvoice.client_business_name || '',
        clientBillingAddress: editingInvoice.client_billing_address || '',
        clientStateCode: editingInvoice.client_state_code || '',
      });
    } else if (isOpen && payment) {
      // Pre-fill from payment
      setFormState((prev) => ({
        ...prev,
        clientId: clientId,
        paymentId: payment.id,
        invoiceDate: payment.paid_at
          ? new Date(payment.paid_at).toISOString().split('T')[0]
          : getCurrentLocalDate(),
        totalAmount: payment.amount.toString(),
        notes: payment.notes || '',
      }));
    }
  }, [isOpen, editingInvoice, payment, clientId]);

  // Calculate tax breakdown for preview
  const taxBreakdown = useMemo(() => {
    const total = parseFloat(formState.totalAmount) || 0;
    const rate = parseFloat(formState.taxRate) || 0;

    if (total === 0 || rate === 0) {
      return {
        baseAmount: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        totalTax: 0,
        totalAmount: 0,
      };
    }

    // Calculate base amount (reverse from total)
    const baseAmount = total / (1 + rate / 100);
    const totalTax = total - baseAmount;

    // For now, assume intra-state (CGST+SGST)
    // In actual implementation, this would check practitioner vs client state codes
    const cgstAmount = totalTax / 2;
    const sgstAmount = totalTax / 2;

    return {
      baseAmount: baseAmount,
      cgstAmount: cgstAmount,
      sgstAmount: sgstAmount,
      igstAmount: 0,
      totalTax: totalTax,
      totalAmount: total,
    };
  }, [formState.totalAmount, formState.taxRate]);

  const handleFieldChange = (field: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formState.totalAmount || parseFloat(formState.totalAmount) <= 0) {
      setFormError('Please enter a valid amount');
      return;
    }

    if (!formState.serviceDescription.trim()) {
      setFormError('Service description is required');
      return;
    }

    if (!formState.invoiceDate) {
      setFormError('Invoice date is required');
      return;
    }

    try {
      const input: CreateInvoiceInput = {
        client_id: formState.clientId,
        payment_id: formState.paymentId || undefined,
        invoice_date: formState.invoiceDate,
        due_date: formState.dueDate || undefined,
        total_amount: parseFloat(formState.totalAmount),
        tax_rate: parseFloat(formState.taxRate),
        hsn_sac_code: formState.hsnSacCode,
        service_description: formState.serviceDescription.trim(),
        notes: formState.notes.trim() || undefined,
        client_gstin: formState.clientGstin.trim() || undefined,
        client_business_name: formState.clientBusinessName.trim() || undefined,
        client_billing_address: formState.clientBillingAddress.trim() || undefined,
        client_state_code: formState.clientStateCode.trim() || undefined,
      };

      if (isEditMode && updateMutation) {
        await updateMutation.mutateAsync(input);
        onSuccess('Invoice updated successfully');
      } else {
        await createMutation.mutateAsync(input);
        onSuccess('Invoice created successfully');
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving invoice:', error);
      const message = error?.message || 'Failed to save invoice';
      onError(message);
      setFormError(message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditMode ? 'Edit Invoice' : 'Generate Tax Invoice'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isLoading}
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Client Info (Read-only) */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
            <div className="text-sm font-semibold text-gray-900">{clientName}</div>
            <div className="text-xs text-gray-500">{clientPhone}</div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Amount (inc. Tax) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formState.totalAmount}
                onChange={(e) => handleFieldChange('totalAmount', e.target.value)}
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                placeholder="5000.00"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Service Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formState.serviceDescription}
              onChange={(e) => handleFieldChange('serviceDescription', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
              placeholder="3-month nutrition consultation"
              rows={3}
              required
              disabled={isLoading}
            />
          </div>

          {/* Invoice Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formState.invoiceDate}
                onChange={(e) => handleFieldChange('invoiceDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={formState.dueDate}
                onChange={(e) => handleFieldChange('dueDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Tax Rate and HSN/SAC */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tax Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formState.taxRate}
                onChange={(e) => handleFieldChange('taxRate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HSN/SAC Code
              </label>
              <input
                type="text"
                value={formState.hsnSacCode}
                onChange={(e) => handleFieldChange('hsnSacCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                placeholder="999311"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Tax Breakdown Preview */}
          {formState.totalAmount && parseFloat(formState.totalAmount) > 0 && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="flex items-start gap-2 mb-2">
                <InfoIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-xs font-medium text-blue-900">
                  Tax Breakdown (auto-calculated)
                </span>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between text-gray-700">
                  <span>Base Amount:</span>
                  <span className="font-medium">
                    ₹{taxBreakdown.baseAmount.toFixed(2)}
                  </span>
                </div>
                {taxBreakdown.cgstAmount > 0 && (
                  <>
                    <div className="flex justify-between text-gray-700">
                      <span>CGST ({parseFloat(formState.taxRate) / 2}%):</span>
                      <span className="font-medium">
                        ₹{taxBreakdown.cgstAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>SGST ({parseFloat(formState.taxRate) / 2}%):</span>
                      <span className="font-medium">
                        ₹{taxBreakdown.sgstAmount.toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
                {taxBreakdown.igstAmount > 0 && (
                  <div className="flex justify-between text-gray-700">
                    <span>IGST ({formState.taxRate}%):</span>
                    <span className="font-medium">
                      ₹{taxBreakdown.igstAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-1 border-t border-blue-300 font-semibold text-blue-900">
                  <span>Total:</span>
                  <span>₹{taxBreakdown.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Client GSTIN (Optional for B2B) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client GSTIN <span className="text-xs text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={formState.clientGstin}
              onChange={(e) => handleFieldChange('clientGstin', e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Required only for B2B clients
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-xs text-gray-500">(optional)</span>
            </label>
            <textarea
              value={formState.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
              placeholder="Payment received via UPI..."
              rows={2}
              disabled={isLoading}
            />
          </div>

          {/* Form Error */}
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{formError}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-[#5B8E87] hover:bg-[#4A7269] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={isLoading}
          >
            {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" />}
            {isEditMode ? 'Update Invoice' : 'Generate Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}
